// Data panels for the Rewards menu. Gated off by REWARDS_MENU_DETAILS_ENABLED until v2 (see config.tsx).
import { Trans, useLingui } from '@lingui/react/macro';
import { Box, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, type ReactNode, useMemo } from 'react';
import { formatNumberDisplay } from '@/utils/bigUtils';
import { USD_CURRENCY_FORMAT } from '@/utils/displayLocale';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';
import { useMyLeaderboardRow } from '../../../features/leaderboard/hooks/useMyLeaderboardRow.ts';
import { useProfileMPoints } from '../../../features/mpoints/hooks/useProfileMPoints.ts';
import {
  computeCampaignStats,
  deriveProfileMPoints,
  mpointsAmountToDisplay,
} from '../../../features/mpoints/utils/mpointsCampaign.ts';
import { useProfilePoints } from '../../../features/points/hooks/useProfilePoints.ts';
import { pointsAmountToDisplay } from '../../../features/points/pointsCampaign.ts';
import { mapReferralStatus } from '../../../features/referral/components/referrals-history/status.ts';
import { useProfileReferrals } from '../../../features/referral/hooks/useProfileReferrals.ts';
import { useProfileRewards } from '../../../features/referral/hooks/useProfileRewards.ts';
import useAppStore from '../../../store/rootStore.ts';
import { getOriginsBadge, originsTierLabel } from '../../../utils/badge.ts';

const PanelRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  padding: theme.spacing(1.25, 1.5),
}));

const PanelHero = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  gap: theme.spacing(0.5),
}));

const BigValue = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 500,
  color: theme.vars.palette.primary.light,
  textAlign: 'center',
  lineHeight: 1.15,
  margin: 0,
}));

const RowsContainer = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  borderTop: `1px solid ${theme.vars.palette.divider}`,
  marginTop: theme.spacing(0.25),
  paddingTop: theme.spacing(0.75),
}));

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.375, 0),
}));

const rowLabelSx = { fontSize: 12, color: 'text.disabled', m: 0 } as const;
const rowValueSx = { fontSize: 12, fontWeight: 500, m: 0 } as const;
const panelLabelSx = {
  fontSize: 12,
  color: 'text.secondary',
  textAlign: 'center',
  m: 0,
  mb: 0.5,
} as const;

const MISSING = '—';
const USD_2DP = { minDecimals: 2, maxDecimals: 2 } as const;

function formatRank(rank: number | null | undefined, formatNumber: (n: number) => string): string {
  if (rank == null || rank <= 0) return MISSING;
  return `#${formatNumber(rank)}`;
}

interface PanelRowData {
  key: string;
  label: ReactNode;
  value: ReactNode;
}

interface PanelLayoutProps {
  label: ReactNode;
  value: ReactNode;
  rows: readonly PanelRowData[];
  footer?: ReactNode;
}

/** Centered hero (label / value) with detail rows pinned to the bottom. */
export const PanelLayout = memo(function PanelLayout({ label, value, rows, footer }: PanelLayoutProps) {
  return (
    <PanelRoot>
      <PanelHero>
        <Typography sx={panelLabelSx}>{label}</Typography>
        <BigValue className="numeric">{value}</BigValue>
      </PanelHero>
      <RowsContainer>
        {rows.map((row) => (
          <Row key={row.key}>
            <Typography sx={rowLabelSx}>{row.label}</Typography>
            <Typography sx={rowValueSx} className="numeric">
              {row.value}
            </Typography>
          </Row>
        ))}
      </RowsContainer>
      {footer}
    </PanelRoot>
  );
});

export const PanelSkeleton = memo(function PanelSkeleton() {
  return (
    <PanelRoot>
      <PanelHero>
        <Skeleton variant="text" width={96} height={16} />
        <Skeleton variant="text" width={120} height={28} />
      </PanelHero>
      <RowsContainer>
        <Skeleton variant="text" height={18} />
        <Skeleton variant="text" height={18} />
        <Skeleton variant="text" height={18} />
      </RowsContainer>
    </PanelRoot>
  );
});

export const PanelDisconnected = memo(function PanelDisconnected() {
  return (
    <PanelRoot sx={{ alignItems: 'center', justifyContent: 'center', gap: 1 }}>
      <Typography sx={{ fontSize: 14, color: 'text.secondary', textAlign: 'center' }}>
        <Trans comment="Rewards menu panel when wallet is disconnected">Connect your wallet to see your rewards</Trans>
      </Typography>
    </PanelRoot>
  );
});

export const MPointsPanel = memo(function MPointsPanel() {
  const { i18n } = useLingui();
  const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => i18n.number(value, options);
  const isFullyAuthorized = useAppStore((s) => s.isFullyAuthorized());
  const rewardsConfig = useAppStore((s) => s.config?.rewards);
  const collateralDecimals = useAppStore((s) => s.exchange?.token?.decimals ?? 6);
  const { data: mpoints, isLoading, isFetching } = useProfileMPoints();

  const campaign = useMemo(() => computeCampaignStats(rewardsConfig, collateralDecimals), [rewardsConfig, collateralDecimals]);
  const derived = useMemo(() => deriveProfileMPoints(mpoints, campaign), [mpoints, campaign]);

  if (!isFullyAuthorized) return <PanelDisconnected />;
  if (isLoading || (isFetching && !mpoints)) return <PanelSkeleton />;

  const totalMPoints = mpointsAmountToDisplay(mpoints?.total_mpoints ?? '0', collateralDecimals);
  const lastSettled = (mpoints?.d ?? []).find((epoch) => epoch.e !== campaign.currentEpoch && epoch.mp != null);
  const earnedLastEpoch = lastSettled ? mpointsAmountToDisplay(lastSettled.mp, collateralDecimals) : 0;
  const rank = lastSettled?.r && lastSettled.r > 0 ? lastSettled.r : null;

  return (
    <PanelLayout
      label={<Trans comment="mPoints panel headline">Your mPoints</Trans>}
      value={formatNumber(totalMPoints)}
      rows={[
        {
          key: 'lastEpoch',
          label: <Trans comment="mPoints last epoch earned row">Last epoch</Trans>,
          value:
            derived.earnedLastEpochPct == null
              ? formatNumber(earnedLastEpoch)
              : `${formatNumber(earnedLastEpoch)} (${derived.earnedLastEpochPct.toFixed(1)}%)`,
        },
        {
          key: 'projected',
          label: <Trans comment="mPoints projected USD row">Projected</Trans>,
          value:
            derived.projectedMonUsd == null
              ? MISSING
              : formatNumber(derived.projectedMonUsd, { ...USD_CURRENCY_FORMAT, maximumFractionDigits: 0 }),
        },
        {
          key: 'rank',
          label: <Trans comment="Shared rewards panel rank row">Rank</Trans>,
          value: formatRank(rank, formatNumber),
        },
      ]}
    />
  );
});

export const PointsPanel = memo(function PointsPanel() {
  const { i18n, t } = useLingui();
  const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => i18n.number(value, options);
  const isFullyAuthorized = useAppStore((s) => s.isFullyAuthorized());
  const showRealPoints = useAppStore((s) => s.config?.features?.showRealPoints ?? false);
  const collateralDecimals = useAppStore((s) => s.exchange?.token?.decimals ?? 6);
  const { data: points, isLoading, isFetching } = useProfilePoints();

  const originsTier = useMemo(() => getOriginsBadge((points?.d ?? []).flatMap((epoch) => epoch.b ?? [])), [points]);

  if (!isFullyAuthorized) return <PanelDisconnected />;
  if (isLoading || (isFetching && !points)) return <PanelSkeleton />;

  const totalPoints = points && showRealPoints ? pointsAmountToDisplay(points.total_points, collateralDecimals) : null;
  const totalRank = points && showRealPoints ? points.total_rank || null : null;
  const lastEpochPoints =
    points && showRealPoints ? pointsAmountToDisplay(points.last_epoch_emission, collateralDecimals) : null;

  return (
    <PanelLayout
      label={<Trans comment="Points panel headline">Your Points</Trans>}
      value={totalPoints == null ? MISSING : formatNumber(totalPoints)}
      rows={[
        {
          key: 'lastEpoch',
          label: <Trans comment="Points last epoch emission row">Last epoch</Trans>,
          value: lastEpochPoints == null ? MISSING : `+${formatNumber(lastEpochPoints)}`,
        },
        {
          key: 'tier',
          label: <Trans comment="Points Origins badge tier row">Origins tier</Trans>,
          value: t(originsTierLabel(originsTier)),
        },
        {
          key: 'rank',
          label: <Trans comment="Shared rewards panel rank row">Rank</Trans>,
          value: formatRank(totalRank, formatNumber),
        },
      ]}
    />
  );
});

export const ReferralsPanel = memo(function ReferralsPanel() {
  const { i18n } = useLingui();
  const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => i18n.number(value, options);
  const isFullyAuthorized = useAppStore((s) => s.isFullyAuthorized());
  const rewards = useAppStore((s) => s.config?.rewards);
  const collateralDecimals = useAppStore((s) => s.exchange?.token?.decimals ?? 6);
  const { data: referrals, isLoading: referralsLoading } = useProfileReferrals();
  const { data: rewardsData, isLoading: rewardsLoading } = useProfileRewards();

  if (!isFullyAuthorized) return <PanelDisconnected />;
  if ((referralsLoading || rewardsLoading) && !referrals && !rewardsData) return <PanelSkeleton />;

  const activeReferees = (referrals?.d ?? []).filter((row) => mapReferralStatus(row.s) === 'active').length;

  const totalPayouts =
    rewardsData?.total_payouts != null ? offsetNumberToNumber(Number(rewardsData.total_payouts), collateralDecimals) : null;
  const liveRewardsAmount = Math.max(Number(referrals?.total_rewards ?? 0) - Number(rewardsData?.total_payouts ?? 0), 0);
  const liveRewards = referrals ? offsetNumberToNumber(liveRewardsAmount, collateralDecimals) : null;

  const feeShareBps = rewards?.referrer_1st_reward_share_bps;
  const feeShare =
    feeShareBps == null ? MISSING : formatNumber(feeShareBps / 10_000, { style: 'percent', maximumFractionDigits: 1 });

  return (
    <PanelLayout
      label={<Trans comment="Referrals panel headline">Referral earnings</Trans>}
      value={totalPayouts == null ? MISSING : `$${formatNumberDisplay(totalPayouts, USD_2DP)}`}
      rows={[
        {
          key: 'thisWeek',
          label: <Trans comment="Referrals live weekly rewards row">This week</Trans>,
          value: liveRewards == null ? MISSING : `$${formatNumberDisplay(liveRewards, USD_2DP)}`,
        },
        {
          key: 'feeShare',
          label: <Trans comment="Referrals fee share row">Fee share</Trans>,
          value: feeShare,
        },
        {
          key: 'activeReferees',
          label: <Trans comment="Referrals active referees row">Active referees</Trans>,
          value: referrals ? formatNumber(activeReferees) : MISSING,
        },
      ]}
    />
  );
});

// v2: not in rewardsMenuItems yet; waits on the header-summary endpoint (avoids full-board downloads).
export const LeaderboardPanel = memo(function LeaderboardPanel() {
  const { i18n, t } = useLingui();
  const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => i18n.number(value, options);
  const myAddress = useAppStore((s) => s.address ?? '').toLowerCase();
  const collateralDecimals = useAppStore((s) => s.exchange?.token?.decimals ?? 6);

  const monthQuery = useMyLeaderboardRow('month', 'vol', collateralDecimals);
  const allQuery = useMyLeaderboardRow('all', 'vol', collateralDecimals);

  if (!myAddress) return <PanelDisconnected />;
  if ((monthQuery.isLoading || allQuery.isLoading) && !monthQuery.row && !allQuery.row) return <PanelSkeleton />;

  const monthRow = monthQuery.row;
  const allRow = allQuery.row;
  const rank = monthRow?.i ?? allRow?.i ?? null;
  const volume = monthRow?.volParsed ?? null;
  const originsTier = getOriginsBadge(monthRow?.b ?? allRow?.b);

  return (
    <PanelLayout
      label={<Trans comment="Leaderboard panel headline">Your rank</Trans>}
      value={formatRank(rank, formatNumber)}
      rows={[
        {
          key: 'volume30d',
          label: <Trans comment="Leaderboard monthly volume row">30d volume</Trans>,
          value:
            volume == null
              ? MISSING
              : formatNumber(volume, {
                  ...USD_CURRENCY_FORMAT,
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
        },
        {
          key: 'tier',
          label: <Trans comment="Leaderboard Origins tier row">Origins tier</Trans>,
          value: t(originsTierLabel(originsTier)),
        },
        {
          key: 'bestRank',
          label: <Trans comment="Leaderboard all-time rank row">All-time rank</Trans>,
          value: formatRank(allRow?.i, formatNumber),
        },
      ]}
    />
  );
});

/** Default panel when no item is hovered. */
export const RewardsSummaryPanel = memo(function RewardsSummaryPanel() {
  const { i18n, t } = useLingui();
  const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => i18n.number(value, options);
  const isFullyAuthorized = useAppStore((s) => s.isFullyAuthorized());
  const showRealPoints = useAppStore((s) => s.config?.features?.showRealPoints ?? false);
  const rewardsEnabled = useAppStore((s) => s.config?.features?.rewardsEnabled);
  const pointsEnabled = useAppStore((s) => s.config?.features?.pointsEnabled);
  const referralDashboardEnabled = useAppStore((s) => s.config?.features?.referralDashboardEnabled);
  const collateralDecimals = useAppStore((s) => s.exchange?.token?.decimals ?? 6);

  const { data: mpoints, isLoading: mpointsLoading } = useProfileMPoints();
  const { data: points, isLoading: pointsLoading } = useProfilePoints();
  const { data: rewardsData, isLoading: rewardsLoading } = useProfileRewards();

  if (!isFullyAuthorized) return <PanelDisconnected />;
  if ((mpointsLoading || pointsLoading || rewardsLoading) && !mpoints && !points && !rewardsData) {
    return <PanelSkeleton />;
  }

  const totalMPoints = rewardsEnabled ? mpointsAmountToDisplay(mpoints?.total_mpoints ?? '0', collateralDecimals) : null;
  const totalPoints =
    pointsEnabled && points && showRealPoints ? pointsAmountToDisplay(points.total_points, collateralDecimals) : null;
  const referralEarnings =
    referralDashboardEnabled && rewardsData?.total_payouts != null
      ? offsetNumberToNumber(Number(rewardsData.total_payouts), collateralDecimals)
      : null;
  const combinedPoints = totalMPoints == null && totalPoints == null ? null : (totalMPoints ?? 0) + (totalPoints ?? 0);
  const pointsUnit = t({ comment: 'Points unit suffix in the rewards summary', message: 'pts' });

  return (
    <PanelLayout
      label={<Trans comment="Rewards summary panel headline">Total rewards</Trans>}
      value={combinedPoints == null ? MISSING : `${formatNumber(combinedPoints)} ${pointsUnit}`}
      rows={[
        {
          key: 'mpoints',
          label: <Trans comment="Rewards menu mPoints item label">mPoints</Trans>,
          value: totalMPoints == null ? MISSING : formatNumber(totalMPoints),
        },
        {
          key: 'points',
          label: <Trans comment="Rewards menu Points item label">Points</Trans>,
          value: totalPoints == null ? MISSING : formatNumber(totalPoints),
        },
        {
          key: 'referrals',
          label: <Trans comment="Referrals panel headline">Referral earnings</Trans>,
          value: referralEarnings == null ? MISSING : `$${formatNumberDisplay(referralEarnings, USD_2DP)}`,
        },
      ]}
    />
  );
});
